import { NoddiCard, useAppForm } from 'noddi-ui-web';
import { z } from 'zod';

const assumptionsSchema = z.object({
  newCustomerGrowthPercent: percentageSchema(-20, 50),
  returningCustomerPercent: percentageSchema(0, 20),
  churnReductionPercent: percentageSchema(-10, 20),
  upsellPercent: percentageSchema(-10, 30),
  additionalMonthlyBookings: z
    .number()
    .nullable()
    .pipe(
      z
        .number({ error: 'Enter monthly bookings' })
        .int('Enter a whole number of bookings')
        .min(0, 'Enter between 0 and 500 bookings')
        .max(500, 'Enter between 0 and 500 bookings')
    )
});

const schema = z.object({
  optimistic: assumptionsSchema,
  pessimistic: assumptionsSchema
});

type ForecastScenarioFormValues = z.input<typeof schema>;

interface Props {
  initialValues: ForecastScenarioFormValues;
  onSubmit: (values: z.output<typeof schema>) => Promise<void>;
}

/** Separate named scenarios prevent multiplier signs from choosing a scenario. Dates belong to the page filters. */
export const NavioForecastScenarioForm = ({ initialValues, onSubmit }: Props) => {
  const form = useAppForm({
    defaultValues: initialValues,
    validators: { onSubmit: schema },
    onSubmit: async ({ value }) => onSubmit(schema.parse(value))
  });
  return (
    <form.AppForm>
      <form
        className='flex flex-col gap-6'
        onSubmit={(event) => {
          event.preventDefault();
          event.stopPropagation();
          void form.handleSubmit();
        }}
      >
        <div className='grid gap-6 lg:grid-cols-2'>
          {scenarios.map(({ key: scenario, title }) => (
            <fieldset key={scenario}>
              <legend className='sr-only'>{title}</legend>
              <NoddiCard title={title} size='small'>
                <div className='flex flex-col gap-5'>
                  <form.AppField name={`${scenario}.newCustomerGrowthPercent`}>
                    {(field) => <field.Input label='New customer growth (%)' type='number' />}
                  </form.AppField>
                  <form.AppField name={`${scenario}.returningCustomerPercent`}>
                    {(field) => <field.Input label='Returning lost customers (%)' type='number' />}
                  </form.AppField>
                  <form.AppField name={`${scenario}.churnReductionPercent`}>
                    {(field) => <field.Input label='Churn reduction (%)' type='number' />}
                  </form.AppField>
                  <form.AppField name={`${scenario}.upsellPercent`}>
                    {(field) => <field.Input label='Upsell effect (%)' type='number' />}
                  </form.AppField>
                  <form.AppField name={`${scenario}.additionalMonthlyBookings`}>
                    {(field) => <field.Input label='Additional monthly bookings' type='number' />}
                  </form.AppField>
                </div>
              </NoddiCard>
            </fieldset>
          ))}
        </div>
        <form.SubscribeButton label='Calculate scenarios' />
      </form>
    </form.AppForm>
  );
};

const scenarios = [
  { key: 'optimistic', title: 'Optimistic assumptions' },
  { key: 'pessimistic', title: 'Pessimistic assumptions' }
] as const;

function percentageSchema(minimum: number, maximum: number) {
  const rangeMessage = `Enter a percentage from ${minimum} to ${maximum}`;
  return z
    .number()
    .nullable()
    .pipe(z.number({ error: 'Enter a percentage' }).min(minimum, rangeMessage).max(maximum, rangeMessage));
}
